import { Component, Input, ViewChild, ElementRef, Renderer2, OnChanges, SimpleChanges } from '@angular/core';
import * as d3 from 'd3';

import { IAssetInfo } from '@interfaces/asset';
import { IAssetName } from '@interfaces/portfolio-constants';
import { AssetsService } from '@services/assets/assets.service';
import Utils from '@util/util';

@Component({
  selector: 'app-market-asset',
  templateUrl: './market-asset.component.html',
  styleUrls: ['./market-asset.component.scss'],
})
export class MarketAssetComponent implements OnChanges {
  @Input() asset: IAssetName;
  @Input() selected: boolean;
  @Input() inMiddleColumn: boolean;
  @Input() assetValue: number;
  @Input() selectedPeriod: string;
  assetPeriodsInfo: IAssetInfo[];
  return: number;
  loaded: boolean;
  uniqueGraphId: string;
  debouncedCalculate: () => void;

  private svg;
  private margin = 10;
  private graphWidth = window.innerWidth > 350 ? window.innerWidth / 4 : window.innerWidth / 4.3;
  private width = this.graphWidth - this.margin * 2;
  private height = this.graphWidth / 2 - this.margin * 2;
  private minPoints1DGraph = 20;

  constructor (
    private assetService: AssetsService,
  ) {
    this.debouncedCalculate = Utils.debounce(this.calculteGraphAndReturn, 100);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.asset && this.assetValue && this.selectedPeriod) {
      this.asset['displayIcon'] = this.assetService
      .buildAssetIconURL(this.asset.assetId, this.asset.drivewealthAsset);
      this.calculteGraphAndReturn();
    }
  }

  async calculteGraphAndReturn() {
    try {
      if (!this.assetPeriodsInfo) {
        this.uniqueGraphId = this.asset.assetId.replace(/[^a-zA-Z0-9]/g, '') + Math.ceil(Math.random() * 1000);
        this.assetPeriodsInfo = await this.assetService.getAssetPeriodsInfo(this.asset.assetId);
        if (this.assetPeriodsInfo && this.assetPeriodsInfo.length > 0) {
          this.assetPeriodsInfo[0].graph.push({sharePrice: this.assetValue});
          this.calculateReturns();
        }
      } else {
        this.calculateReturns();
      }
    } catch (error) {
      return null;
    }
  }

  calculateReturns() {
    const info = this.assetPeriodsInfo.find(p => p.period === this.selectedPeriod);
    const variable = this.assetValue / info.comparePrice - 1;
    this.return = Math.round(variable * 10000) / 100;
    const rentability = this.return ? this.return : variable;
    this.loaded = true;
    setTimeout(() => {
      this.createSvg();
      this.drawBars(info.graph, rentability, info.period);
    });
  }

  private createSvg(): void {
    if (this.inMiddleColumn) {
      this.width = 70;
      this.height = 30;
    }
    d3.select(`figure#graph-${this.uniqueGraphId}`)
      .select('svg')
      .remove();
    this.svg = d3
      .select(`figure#graph-${this.uniqueGraphId}`)
      .append('svg')
      .attr('width', this.width + this.margin * 2)
      .attr('height', this.height + this.margin * 2)
      .append('g')
      .attr('transform', 'translate(' + this.margin + ',' + this.margin + ')');
  }

  private drawBars(data: any[], rentability: number, name: string): void {
    let min = Number.MAX_VALUE;
    let max = -Number.MAX_VALUE;
    let counter = -1;
    if (data.some(a => isNaN(a))) {
      data = data.map(d => {
      counter += 1;
      return {
        sharePrice: d.sharePrice || d,
        position: counter,
      };
    });
    } else {
      data = data.map(d => {
        counter += 1;
        return {
          sharePrice: d,
          position: counter,
        };
      });
    }
    data.forEach(o => {
      Object.keys(o).forEach(k => {
        if (k !== 'date' && k !== 'position') {
          min = Math.min(min, o[k]);
          max = Math.max(max, o[k]);
        }
      });
    });
    let x2 = data.length - 1;
    if (name === '1D' || name === 'MTD' || name === 'YTD') {
      x2 = Math.max(this.minPoints1DGraph, data.length - 1);
    }
    const x = d3
      .scaleLinear()
      .domain([0, x2])
      .range([0, this.width]);
    this.svg
      .append('g')
      .attr('transform', 'translate(0,' + this.height + ')')
      .call(g => g.select('.domain').remove());
    const y = d3
      .scaleLinear()
      .domain([min - 1, max + 1])
      .range([this.height, 0]);
    this.svg.append('g').call(g => g.select('.domain').remove());
    const color = rentability >= -0.005 ? '#18daae' : '#e8581e';
    this.svg
      .append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', color)
      .attr('stroke-width', 1.5)
      .attr(
        'd',
        d3
          .line()
          .x((d: any) => x(d.position))
          .y((d: any) => y(d.sharePrice)),
      );
    this.svg
      .append('line')
      .style('stroke-dasharray', '3,2')
      .style('stroke', '#D8D8D8')
      .attr('class', 'mean-line')
      .attr('x1', x(0))
      .attr('y1', y(data[0].sharePrice))
      .attr('x2', x(x2))
      .attr('y2', y(data[0].sharePrice));
  }

}
